import { useEffect, useState } from 'react';
import api from '../services/api';

export type StoreMode = 'single' | 'catalog';

export interface StoreModeResult {
  mode: StoreMode;
  productId?: number;
  productSlug?: string;
  forced?: boolean;
}

interface UseStoreModeState extends StoreModeResult {
  loading: boolean;
}

const CACHE_KEY = 'storeMode_v1';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — picks up wizard changes quickly

interface CachedEntry {
  host: string;
  payload: StoreModeResult;
  ts: number;
}

const readCache = (host: string): StoreModeResult | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as CachedEntry;
    if (c.host !== host) return null;
    if (Date.now() - c.ts > CACHE_TTL_MS) return null;
    return c.payload;
  } catch {
    return null;
  }
};

const writeCache = (host: string, payload: StoreModeResult) => {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ host, payload, ts: Date.now() } as CachedEntry),
    );
  } catch {
    /* ignore quota / private mode */
  }
};

/**
 * Resolves the storefront mode for the current host.
 *
 *   - 'single'  → render SingleProductPage everywhere
 *   - 'catalog' → render normal storefront grid + PDP
 *
 * Result cached in sessionStorage for the session (5min TTL).
 */
export function useStoreMode(): UseStoreModeState {
  const host =
    typeof window !== 'undefined' ? window.location.hostname : '';

  const cached = host ? readCache(host) : null;

  const [state, setState] = useState<UseStoreModeState>(
    cached
      ? { ...cached, loading: false }
      : { mode: 'catalog', loading: true },
  );

  useEffect(() => {
    if (!host) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/storefront/mode', {
          params: { domain: host },
        });
        if (!alive) return;
        const payload = res.data as StoreModeResult;
        writeCache(host, payload);
        setState({ ...payload, loading: false });
      } catch {
        if (!alive) return;
        // On error, default to catalog (safe — exposes more, less risk).
        setState({ mode: 'catalog', loading: false });
      }
    })();
    return () => {
      alive = false;
    };
  }, [host]);

  return state;
}
